import {apiFetch} from "./client";

export type MerchantWithdrawalStatus = "requested" | "accepted" | "sending" | "completed" | "rejected";
export type MerchantWithdrawalNetwork = "trc20" | "bep20";

export interface MerchantWithdrawal {
  id: string;
  merchantId: string;
  merchantName: string;
  amount: string;
  received: string | null;
  fee: string | null;
  address: string;
  network: MerchantWithdrawalNetwork;
  feePolicy?: "gasfree_included" | "sender_pays_bnb";
  status: MerchantWithdrawalStatus;
  executorId?: string | null;
  txHash?: string | null;
  verificationReason?: string | null;
  createdAt: string;
  updatedAt: string;
  sendStartedAt?: string | null;
  rejectionReason?: string | null;
}

export interface MerchantWithdrawalAvailability {
  eligible: boolean;
  reason?: string;
  balance: string;
  reserved: string;
  available: string | null;
  currency: "USDT";
  network: "trc20";
  networks?: {network: MerchantWithdrawalNetwork; feePolicy: "gasfree_included" | "sender_pays_bnb"}[];
}

export interface MerchantWithdrawalsResponse {
  items: MerchantWithdrawal[];
  availability: MerchantWithdrawalAvailability;
}

export interface AdminMerchantWithdrawalsResponse { items: MerchantWithdrawal[]; }

export interface CreateMerchantWithdrawalRequest {
  clientRequestId: string;
  amount: string;
  address: string;
  network?: MerchantWithdrawalNetwork;
}

export function listMerchantWithdrawals(token: string | null, signal?: AbortSignal): Promise<MerchantWithdrawalsResponse> {
  return apiFetch<MerchantWithdrawalsResponse>("/merchant/me/withdrawals", token, {signal});
}

export function createMerchantWithdrawal(
  token: string | null,
  payload: CreateMerchantWithdrawalRequest,
): Promise<MerchantWithdrawal> {
  return apiFetch<MerchantWithdrawal>("/merchant/me/withdrawals", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listAdminMerchantWithdrawals(token: string | null, merchantId?: string, signal?: AbortSignal): Promise<AdminMerchantWithdrawalsResponse> {
  const query = merchantId?.trim() ? `?merchantId=${encodeURIComponent(merchantId.trim())}` : "";
  return apiFetch<AdminMerchantWithdrawalsResponse>(`/admin/merchant-withdrawals${query}`, token, {signal});
}

export function acceptMerchantWithdrawal(token: string | null, id: string): Promise<MerchantWithdrawal> {
  return adminCommand(token, id, "accept", {});
}

export function startMerchantWithdrawal(token: string | null, id: string): Promise<MerchantWithdrawal> {
  return adminCommand(token, id, "start", {});
}

export function recordMerchantWithdrawalTransaction(token: string | null, id: string, txHash: string): Promise<MerchantWithdrawal> {
  return adminCommand(token, id, "transaction", {txHash});
}

export function rejectMerchantWithdrawal(token: string | null, id: string, reason: string): Promise<MerchantWithdrawal> {
  return adminCommand(token, id, "reject", {reason});
}

function adminCommand(token: string | null, id: string, action: string, body: unknown): Promise<MerchantWithdrawal> {
  return apiFetch<MerchantWithdrawal>(`/admin/merchant-withdrawals/${encodeURIComponent(id)}/${action}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
